import {
  ApolloError,
  FetchResult,
  MutationFunctionOptions,
  MutationHookOptions,
  MutationTuple,
  OperationVariables,
  useMutation as useApolloMutation,
} from "@apollo/client";
import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { useToast } from "@resource/atlas";
import { ErrorCodes } from "@resource/common";
import * as Sentry from "@sentry/nextjs";
import { DocumentNode } from "graphql";
import _ from "lodash";
import { useRouter } from "next/router";
import { useCallback, useMemo } from "react";

export enum ToastDisplayCondition {
  NONE,
  ERROR,
  SUCCESS,
  ALL,
}

interface AdditionalOptions {
  displayToastsOn?: ToastDisplayCondition;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useMutation = <TData = any, TVariables = OperationVariables>(
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: MutationHookOptions<TData, TVariables> & AdditionalOptions
): MutationTuple<TData, TVariables> => {
  const { sendToast } = useToast();
  const router = useRouter();
  const [mutate, mutationData] = useApolloMutation<TData, TVariables>(
    mutation,
    options
  );

  const displayToastsOn = options?.displayToastsOn ?? ToastDisplayCondition.ALL;

  const mutateWrapped = useCallback(
    async (wrappedOptions?: MutationFunctionOptions<TData, TVariables>) => {
      try {
        const res = await mutate(wrappedOptions);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        _.forEach<FetchResult<TData, Record<string, any>, Record<string, any>>>(
          res.data,
          (mutationResponse) => {
            if (
              mutationResponse &&
              "success" in mutationResponse &&
              "message" in mutationResponse
            ) {
              const { message, success } = mutationResponse;
              if (
                displayToastsOn === ToastDisplayCondition.ALL ||
                (success &&
                  displayToastsOn === ToastDisplayCondition.SUCCESS) ||
                (!success && displayToastsOn === ToastDisplayCondition.ERROR)
              ) {
                sendToast(message, {
                  variant: success ? "success" : "error",
                });
              }
            }
          }
        );
        return res;
      } catch (err) {
        if (
          err instanceof ApolloError &&
          (displayToastsOn === ToastDisplayCondition.ALL ||
            displayToastsOn === ToastDisplayCondition.ERROR)
        ) {
          Sentry.captureException(err);

          for (let i = 0; i < err.graphQLErrors.length; i += 1) {
            const graphqlError = err.graphQLErrors[i];
            const explicitErrorCode = graphqlError?.extensions?.exception?.code;
            if (explicitErrorCode) {
              switch (explicitErrorCode) {
                case ErrorCodes.VALIDATION_ERROR:
                  sendToast(graphqlError.message, {
                    variant: "error",
                  });
                  break;
                default:
                  break;
              }
            }
            // TODO: Verify if this existing code works as intended as I don't think it does other than the INTERNAL_SERVER_ERROR (Salman)
            if (!explicitErrorCode && graphqlError?.extensions?.code) {
              switch (graphqlError.extensions.code) {
                case "UNAUTHENTICATED":
                  sendToast(
                    "It appears you were logged out. Please login again!",
                    {
                      variant: "error",
                    }
                  );
                  router.push("/login");
                  break;
                case "BAD_USER_INPUT":
                  sendToast(graphqlError.message, {
                    variant: "error",
                  });
                  break;
                case "FORBIDDEN":
                  sendToast(
                    "It looks like you don't have permission to do that.",
                    {
                      variant: "error",
                    }
                  );
                  break;
                case "RATE_LIMIT_EXCEEDED":
                  sendToast(
                    "It looks like you've made too many requests. Please try again soon.",
                    {
                      variant: "error",
                    }
                  );
                  break;
                case "INTERNAL_SERVER_ERROR":
                  sendToast(
                    "Looks like something went wrong. Try getting in touch with us.",
                    {
                      variant: "error",
                    }
                  );
                  break;
                default:
                  break;
              }
            }
          }
        }
        throw err;
      }
    },
    [displayToastsOn, mutate, router, sendToast]
  );

  return useMemo(
    () => [mutateWrapped, mutationData],
    [mutateWrapped, mutationData]
  );
};

export default useMutation;
